import { Timeline } from '@mantine/core'
import type { IconProps } from '@tabler/icons-react'
import {
  IconNumber1,
  IconNumber2,
  IconNumber3,
  IconNumber4,
  IconNumber5,
  IconNumber6,
  IconNumber7,
} from '@tabler/icons-react'
import type { ComponentType } from 'react'

import { theme } from '@/configs/theme'
import useCheckMobileScreen from '@/hooks/useCheckMobileScreen'
import * as classes from '@/styles/SectionedForm.css'

import type { CollapsibleContentSectionProps } from './CollapsibleContentSection'
import { CollapsibleContentSection } from './CollapsibleContentSection'

export type SectionedFormProps = {
  sections: CollapsibleContentSectionProps[]
}

const iconRegistry = {
  1: IconNumber1,
  2: IconNumber2,
  3: IconNumber3,
  4: IconNumber4,
  5: IconNumber5,
  6: IconNumber6,
  7: IconNumber7,
}

export function SectionedForm(props: SectionedFormProps) {
  const { sections } = props
  const isMobile = useCheckMobileScreen()

  // Only consider visible sections when calculating active index
  const visibleSections = sections.filter(s => !s.hidden)
  let activeIndex = visibleSections.findIndex(s => s.valid === false)
  activeIndex = activeIndex === -1 ? visibleSections.length - 1 : activeIndex

  const timelineItems = sections.map((s, index, arr) => {
    const skipCount = arr.slice(0, index).filter(s => s.skipTimelineBullet || s.hidden).length

    const Icon: ComponentType<IconProps> = iconRegistry[(index - skipCount + 1) as keyof typeof iconRegistry]

    if (s.hidden) return null

    return {
      section: s,
      icon: Icon,
      key: s.id,
    }
  })

  // On mobile, render sections without timeline
  if (isMobile) {
    return (
      <>
        {sections.map(s => {
          if (s.hidden) return null
          return <CollapsibleContentSection key={s.id} {...s} />
        })}
      </>
    )
  }

  // Desktop: render with timeline
  return (
    <Timeline active={activeIndex} bulletSize={36} lineWidth={3}>
      {timelineItems.map(item => {
        if (!item) return null

        const { section: s, icon: Icon, key } = item

        return (
          <Timeline.Item
            key={key}
            bullet={<Icon size={18} />}
            data-timeline-item-hidden={s.hidden}
            classNames={{
              item: classes.item,
              itemBullet: theme.cx(classes.itemBullet, s.skipTimelineBullet && classes.itemBulletHidden),
            }}>
            <CollapsibleContentSection {...s} />
          </Timeline.Item>
        )
      })}
    </Timeline>
  )
}
